import React, { useEffect, useState } from "react";
import type { ColumnsType } from "antd/es/table";
import { Button, Col, Collapse, Pagination, Row, Table } from "antd";
import moment from "moment";
import { useAppSelector } from "../..";
import {
  PriceValidationCode,
  PriceValidationStatus,
  PriceValidationResult,
  PriceMessage,
  PriceImportStatus,
} from "../../lib/types";
import {
  priceValidationCodes,
  priceValidationStatuses,
  priceImportStatuses,
} from "../../lib/const";
import { uploadPriceFormat, displayFormat } from "../../lib/utils/formatDate";
import { PriceItemValidationIcon } from "./PriceItemValidationIcon";
import { SpanLimitText } from "../common/SpanLimitText";
import TablePageSelect from "../TablePageSelect/TablePageSelect";
import { checkIsLoading } from "../../lib/utils";
import SpanNumberFormat from "../common/SpanNumberFormat";
import "./UploadPrice.less";

type PriceСomparisonStatus = Extract<PriceValidationStatus, "equal" | "increased" | "decreased">;
type DateСomparisonStatus = Extract<PriceValidationStatus, "equal" | "earlierDate" | "laterDate">;

type AreasCodeСomparisonStatus = Extract<PriceValidationStatus, "equal" | "updated">;

interface Data {
  key: number;
  hasExistedDestination: boolean;
  countryCode?: string;
  countryNotDefined: boolean;
  newName?: string;
  newAreasCode?: string;
  areasCodeСomparison: { status: AreasCodeСomparisonStatus; title: string };
  existedName?: string;
  existedAreasCode?: string;
  newPrice?: number;
  priceError?: PriceMessage;
  priceСomparison: { status: PriceСomparisonStatus; title: string };
  existedPrice?: number;
  newFromDate?: string;
  dateComparision: { status: DateСomparisonStatus; title: string };
  period?: string;
}

interface ClosingDestinationData {
  key: number;
  name?: string;
  areasCode?: string;
  price?: number;
  fromDate?: string;
  toDate?: string;
}

const priceErrorCodes = [
  priceValidationCodes.noChangePriceChanged,
  priceValidationCodes.decreasePriceIncreased,
  priceValidationCodes.increasePriceDecreased,
  priceValidationCodes.changePriceNotChanged,
];

const checkPriceValidationStatus = (
  message: PriceMessage[],
  priceValidationStatus: PriceValidationCode
): boolean => message.some(({ code }) => code === priceValidationStatus);

const priceComparisonTitle: Record<PriceСomparisonStatus, string> = {
  equal: "Цена не изменилась",
  increased: "Цена увеличилась",
  decreased: "Цена уменьшилась",
};

const dateComparisonTitle: Record<DateСomparisonStatus, string> = {
  equal: "Дата не изменилась",
  earlierDate: "Дата изменилась на более раннюю",
  laterDate: "Дата изменилась на более позднюю",
};

const areasCodeСomparisonTitle: Record<AreasCodeСomparisonStatus, string> = {
  equal: "Коды не изменились",
  updated: "Коды обновлены",
};

const gePriceСomparisonStatus = (props: {
  newPrice?: number;
  existedPrice?: number;
  message: PriceMessage[];
}): PriceСomparisonStatus => {
  const { newPrice, existedPrice, message } = props;
  const priceDifference = (newPrice || 0) - (existedPrice || 0);
  const priceIncreased = checkPriceValidationStatus(message, priceValidationCodes.priceIncreased);
  const priceDecreased = checkPriceValidationStatus(message, priceValidationCodes.priceDecreased);
  if (priceDifference > 0 || priceIncreased) return "increased";
  if (priceDifference < 0 || priceDecreased) return "decreased";
  return "equal";
};

const getDisplayFormatDate = (date?: string): string | undefined =>
  moment(date, uploadPriceFormat)?.format(displayFormat);

interface PaginationOptions {
  page: number;
  size: number;
  onChange: (params: { page: number; size: number }) => void;
}

interface UploadPriceResultProps {
  codesTextLimit: number;
  handleDeletePriceImport: () => void;
  handleSavePriceImport: () => void;
  activeImportStatusId?: PriceImportStatus;
  paginationOptions: PaginationOptions;
  closingPaginationOptions: PaginationOptions;
}

/* ---------------------------------------------- Начало компонента ---------------------------------------------- */

export const UploadPriceResult = (props: UploadPriceResultProps) => {
  const {
    codesTextLimit,
    handleDeletePriceImport,
    handleSavePriceImport,
    activeImportStatusId,
    paginationOptions,
    closingPaginationOptions,
  } = props;
  const [data, setData] = useState<Data[]>([]);
  // console.log(priceValidationResult[0]);
  // console.log(data);
  const [closingDestinationData, setClosingDestinationData] = useState<ClosingDestinationData[]>(
    []
  );
  // console.log(closingDestinationData);

  const {
    priceValidationResult,
    priceValidationResultTotalElements,
    priceValidationResultLoading,
    priceValidationClosingResult,
    priceValidationClosingResultTotalElements,
    priceValidationClosingResultLoading,
  } = useAppSelector((state) => state.individualTariffs);

  // Заполнение данных для отображения результатов валидации
  useEffect(() => {
    setData(
      priceValidationResult
        .filter(({ data }) => Boolean(data.newDestination) || Boolean(data.existedDestination))
        .map(({ id, data, message }) => {
          const { newDestination, existedDestination } = data;
          const countryNotDefined = checkPriceValidationStatus(
            message,
            priceValidationCodes.countryNotDefined
          );
          const newAreasCode = newDestination?.areas.map(({ code }) => code).join(", ");
          const areasCodeСomparisonStatus: AreasCodeСomparisonStatus = checkPriceValidationStatus(
            message,
            priceValidationCodes.codeListChanged
          )
            ? "updated"
            : "equal";
          const newDirection = checkPriceValidationStatus(
            message,
            priceValidationCodes.newDirection
          );
          const existedName = newDirection ? undefined : data.existedDestination?.name;
          const existedAreasCode = newDirection
            ? undefined
            : existedDestination?.areas.map(({ code }) => code).join(", ");
          const newPrice = newDestination?.offeringItem.rateRuleItem.valNum;
          // @ts-ignore
          const priceError = message.find(({ code }) => priceErrorCodes.includes(code));
          const existedPrice = existedDestination?.offeringItem.rateRuleItem.valNum;
          const priceСomparisonStatus = gePriceСomparisonStatus({
            newPrice,
            existedPrice,
            message,
          });
          const areasFromDate = newDestination?.areas?.map(({ fromDate }) =>
            moment(fromDate, uploadPriceFormat)
          );
          const newFromDate = areasFromDate
            ? moment.min(areasFromDate).format(displayFormat)
            : undefined;
          const dateComparisionStatus: DateСomparisonStatus = (() => {
            if (message.some(({ code }) => code === priceValidationCodes.earlierDate)) {
              return "earlierDate";
            }
            if (message.some(({ code }) => code === priceValidationCodes.laterDate)) {
              return "laterDate";
            }
            return "equal";
          })();
          const period = newDirection
            ? undefined
            : `${getDisplayFormatDate(
                existedDestination?.offeringItem.rateRuleItem.fromDate
              )} - ${getDisplayFormatDate(existedDestination?.offeringItem.rateRuleItem.toDate)}`;

          return {
            key: id,
            hasExistedDestination: Boolean(existedDestination),
            countryCode: newDestination?.countryCode,
            countryNotDefined,
            newName: newDestination?.name,
            newAreasCode,
            areasCodeСomparison: {
              status: areasCodeСomparisonStatus,
              title: areasCodeСomparisonTitle[areasCodeСomparisonStatus],
            },
            existedName,
            existedAreasCode,
            newPrice,
            priceError,
            priceСomparison: {
              status: priceСomparisonStatus,
              title: priceComparisonTitle[priceСomparisonStatus],
            },
            existedPrice: newDirection ? undefined : existedPrice,
            newFromDate,
            dateComparision: {
              status: dateComparisionStatus,
              title: dateComparisonTitle[dateComparisionStatus],
            },
            period,
          };
        })
    );
  }, [priceValidationResult]);

  // Заполнение данных для отображения закрываемых направлении из тарифа
  useEffect(() => {
    setClosingDestinationData(
      priceValidationClosingResult
        .filter(({ data }) => Boolean(data.closingDestination))
        .map(({ id, data }) => {
          // const { newDestination: closingDestination } = data;
          const { closingDestination } = data;
          // console.log(closingDestination);
          const areasCode = closingDestination?.areas.map(({ code }) => code).join(", ");
          const fromDate = getDisplayFormatDate(
            closingDestination?.offeringItem.rateRuleItem.fromDate
          );
          const toDate = getDisplayFormatDate(closingDestination?.offeringItem.rateRuleItem.toDate);
          return {
            key: id,
            name: closingDestination?.name,
            areasCode,
            price: closingDestination?.offeringItem.rateRuleItem.valNum,
            fromDate,
            toDate,
          };
        })
    );
  }, [priceValidationClosingResult]);

  const columns: ColumnsType<Data> = [
    {
      title: "Код страны",
      key: "countryCode",
      render: (v, { countryCode, countryNotDefined }) =>
        countryNotDefined ? (
          <PriceItemValidationIcon
            priceItemValidationStatus={priceValidationStatuses.notDefined}
            title="Страна не определена"
          />
        ) : (
          <SpanLimitText text={countryCode} limit={codesTextLimit} />
        ),
    },
    {
      title: "Направление",
      key: "newName",
      dataIndex: "newName",
    },
    {
      title: "Коды",
      key: "newAreasCode",
      render: (v, { newAreasCode, areasCodeСomparison }) => {
        const style = areasCodeСomparison.status === "updated" ? { color: "red" } : {};
        return <SpanLimitText text={newAreasCode} limit={codesTextLimit} style={style} />;
      },
    },
    {
      key: "areasCodeСomparison",
      render: (v, { areasCodeСomparison, hasExistedDestination }) =>
        hasExistedDestination ? (
          <PriceItemValidationIcon
            priceItemValidationStatus={areasCodeСomparison.status}
            title={areasCodeСomparison.title}
          />
        ) : null,
    },
    {
      title: "Направление в тарифе",
      key: "existedName",
      dataIndex: "existedName",
    },
    {
      title: "Коды в тарифе",
      key: "existedAreasCode",
      render: (v, { existedAreasCode, areasCodeСomparison }) => {
        const style = areasCodeСomparison.status === "updated" ? { color: "red" } : {};
        return <SpanLimitText text={existedAreasCode} limit={codesTextLimit} style={style} />;
      },
    },
    {
      title: "Цена",
      key: "newPrice",
      dataIndex: "newPrice",
      render: (newPrice) => <SpanNumberFormat amount={newPrice} fractSize={4} />,
    },
    {
      key: "priceError",
      render: (v, { priceError }) =>
        priceError ? (
          <PriceItemValidationIcon priceItemValidationStatus="error" title={priceError.message} />
        ) : null,
    },
    {
      key: "priceСomparison",
      render: (v, { priceСomparison, hasExistedDestination }) =>
        hasExistedDestination ? (
          <PriceItemValidationIcon
            priceItemValidationStatus={priceСomparison.status}
            title={priceСomparison.title}
          />
        ) : null,
    },
    {
      title: "Цена в тарифе",
      key: "existedPrice",
      dataIndex: "existedPrice",
      render: (existedPrice) => <SpanNumberFormat amount={existedPrice} fractSize={4} />,
    },
    {
      title: "Дата с",
      key: "newFromDate",
      dataIndex: "newFromDate",
    },
    {
      key: "dateComparision",
      render: (v, { dateComparision, hasExistedDestination }) =>
        hasExistedDestination ? (
          <PriceItemValidationIcon
            priceItemValidationStatus={dateComparision.status}
            title={dateComparision.title}
          />
        ) : null,
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
    },
  ];

  const closingDestinationColumns: ColumnsType<ClosingDestinationData> = [
    {
      title: "Направление в тарифе",
      key: "name",
      dataIndex: "name",
    },
    {
      title: "Коды в тарифе",
      key: "areasCode",
      render: (v, { areasCode }) => <SpanLimitText text={areasCode} limit={codesTextLimit} />,
    },
    {
      title: "Цена в тарифе",
      key: "price",
      dataIndex: "price",
      render: (price) => <SpanNumberFormat amount={price} fractSize={4} />,
    },
    {
      title: "Период",
      key: "period",
      render: (v, { fromDate, toDate }) => `${fromDate} - ${toDate}`,
    },
  ];

  return (
    <div className="upload-price upload-price-result">
      {closingDestinationData.length > 0 ? (
        <Collapse expandIconPosition="right" bordered={false}>
          <Collapse.Panel
            header={<span style={{ color: "red" }}>Направления к закрытию</span>}
            key="1"
          >
            <Row justify="space-between">
              <Col />
              <Col offset={5}>
                <Pagination
                  onChange={(page) =>
                    closingPaginationOptions.onChange({ page, size: closingPaginationOptions.size })
                  }
                  current={closingPaginationOptions.page}
                  total={priceValidationClosingResultTotalElements}
                  pageSize={closingPaginationOptions.size}
                  size="small"
                  showSizeChanger={false}
                  style={{ marginTop: "4px" }}
                />
              </Col>
              <Col>
                <TablePageSelect
                  onChange={(size: number) => closingPaginationOptions.onChange({ size, page: 1 })}
                  value={closingPaginationOptions.size}
                  pageSizeOptions={{ 10: 10, 20: 20, 50: 50, 100: 100 }}
                />
              </Col>
            </Row>
            <Table
              size="small"
              dataSource={closingDestinationData}
              columns={closingDestinationColumns}
              pagination={{
                total: priceValidationClosingResultTotalElements,
                pageSize: closingPaginationOptions.size,
                current: closingPaginationOptions.page,
                style: { display: "none" },
              }}
              loading={checkIsLoading(priceValidationClosingResultLoading)}
            />
          </Collapse.Panel>
        </Collapse>
      ) : (
        <span>Направлений к закрытию нет</span>
      )}
      <Row justify="space-between">
        <Col />
        <Col offset={5}>
          <Pagination
            onChange={(page) => paginationOptions.onChange({ page, size: paginationOptions.size })}
            current={paginationOptions.page}
            total={priceValidationResultTotalElements}
            pageSize={paginationOptions.size}
            size="small"
            showSizeChanger={false}
            style={{ marginTop: "4px" }}
          />
        </Col>
        <Col>
          <TablePageSelect
            onChange={(size: number) => paginationOptions.onChange({ size, page: 1 })}
            value={paginationOptions.size}
            pageSizeOptions={{ 10: 10, 20: 20, 50: 50, 100: 100 }}
          />
        </Col>
      </Row>
      <Table
        size="small"
        dataSource={data}
        columns={columns}
        pagination={{
          total: priceValidationResultTotalElements,
          pageSize: paginationOptions.size,
          current: paginationOptions.page,
          style: { display: "none" },
        }}
        loading={checkIsLoading(priceValidationResultLoading)}
      />
      <Row gutter={20} justify="center" style={{ margin: "20px" }}>
        <Col>
          <Button onClick={handleDeletePriceImport} className="xl-btn">
            Отменить импорт
          </Button>
        </Col>
        {activeImportStatusId !== priceImportStatuses.importValidateError && (
          <Col>
            <Button type="primary" onClick={handleSavePriceImport} className="l-btn">
              Сохранить
            </Button>
          </Col>
        )}
      </Row>
    </div>
  );
};
